import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import SelectUI from '../../../components/ui/Select';


const BookingForm = ({ service, consultant, dateTime, loading, onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    purpose: '',
    urgency: 'normal',
    notes: '',
    preparation: '',
    language: 'français'
  });
  const [errors, setErrors] = useState({});

  const urgencyOptions = [
    { value: 'low',      label: 'Non urgent' },
    { value: 'normal',   label: 'Normal' },
    { value: 'high',     label: 'Urgent' },
    { value: 'critical', label: 'Très urgent' },
  ];

  const languageOptions = [
    { value: 'français', label: 'Français' },
    { value: 'english',  label: 'English' },
  ];

  const purposeOptions = (service?.id === 'property_consultation' ? [
    'Analyse de marché immobilier',
    'Conseil en investissement',
    'Évaluation de propriété',
    'Recherche de propriété',
    'Négociation assistée',
    'Autre'
  ] : [
    'Vérification de documents',
    'Conseil transactionnel',
    'Préparation d\'actes',
    'Assistance juridique',
    'Services notariés',
    'Autre'
  ])?.map(o => ({ value: o, label: o }));

  const set = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData?.firstName?.trim()) e.firstName = 'Le prénom est requis';
    if (!formData?.lastName?.trim())  e.lastName  = 'Le nom est requis';
    if (!formData?.email?.trim())     e.email     = 'L\'email est requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) e.email = 'Email invalide';
    if (!formData?.phone?.trim())     e.phone     = 'Le téléphone est requis';
    if (!formData?.purpose?.trim())   e.purpose   = 'L\'objet de la consultation est requis';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) onSubmit?.(formData);
  };

  const formatDateTime = () => {
    if (!dateTime) return '';
    return new Date(dateTime)?.toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const consultantName = consultant?.name || consultant?.notary_name || consultant?.user_profile?.full_name || 'Consultant';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={onBack}>
          Retour
        </Button>
        <div>
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Informations de contact
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Finalisez votre réservation avec {consultantName}
          </p>
        </div>
      </div>

      {/* Booking summary */}
      <div className="bg-muted/40 border border-border rounded-lg px-5 py-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Récapitulatif
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-muted-foreground mb-0.5">Service</p>
            <p className="font-medium text-foreground">{service?.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Consultant</p>
            <p className="font-medium text-foreground">{consultantName}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Date & Heure</p>
            <p className="font-medium text-foreground capitalize">{formatDateTime()}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Durée</p>
            <p className="font-medium text-foreground">{service?.duration} min</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Personal info */}
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">1</span>
            Informations personnelles
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              required
              placeholder="Votre prénom"
              value={formData?.firstName}
              onChange={(e) => set('firstName', e?.target?.value)}
              error={errors?.firstName}
            />
            <Input
              label="Nom de famille"
              required
              placeholder="Votre nom"
              value={formData?.lastName}
              onChange={(e) => set('lastName', e?.target?.value)}
              error={errors?.lastName}
            />
            <Input
              type="email"
              label="Adresse email"
              required
              placeholder="vous@exemple.com"
              value={formData?.email}
              onChange={(e) => set('email', e?.target?.value)}
              error={errors?.email}
            />
            <Input
              type="tel"
              label="Téléphone"
              required
              placeholder="+225 XX XX XX XX XX"
              value={formData?.phone}
              onChange={(e) => set('phone', e?.target?.value)}
              error={errors?.phone}
            />
          </div>
        </div>

        {/* Consultation details */}
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">2</span>
            Détails de la consultation
          </p>

          <SelectUI
            label="Objet de la consultation"
            required
            options={purposeOptions}
            value={formData?.purpose}
            onChange={(v) => set('purpose', v)}
            placeholder="Sélectionnez l'objet"
            error={errors?.purpose}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectUI
              label="Niveau d'urgence"
              options={urgencyOptions}
              value={formData?.urgency}
              onChange={(v) => set('urgency', v)}
            />
            <SelectUI
              label="Langue préférée"
              options={languageOptions}
              value={formData?.language}
              onChange={(v) => set('language', v)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Notes supplémentaires
            </label>
            <textarea
              rows={3}
              placeholder="Informations complémentaires pour votre consultant…"
              value={formData?.notes}
              onChange={(e) => set('notes', e?.target?.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-body ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-none"
            />
          </div>
        </div>

        {/* Preparation */}
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">3</span>
            Préparation
          </p>
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Documents à préparer
            </label>
            <textarea
              rows={3}
              placeholder="Listez les documents que vous souhaitez apporter ou discuter…"
              value={formData?.preparation}
              onChange={(e) => set('preparation', e?.target?.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-body ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-none"
            />
          </div>
        </div>

        {/* Global form error summary */}
        {Object.keys(errors)?.length > 0 && (
          <div className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Veuillez corriger les erreurs ci-dessus avant de soumettre.</span>
          </div>
        )}

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <Button
            type="button"
            variant="outline"
            size="default"
            onClick={onBack}
            className="sm:w-auto"
          >
            Retour
          </Button>
          <Button
            type="submit"
            variant="default"
            size="default"
            loading={loading}
            iconName="CalendarCheck"
            iconPosition="right"
            className="flex-1"
          >
            Confirmer la réservation
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;